// frontend/src/widgets/AdvancedUserListWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-06-20
// Updated:      2026-09-07 12:04 BST
//
// Description:
//   Advanced user list with column chooser (portaled), drag/drop column
//   reordering, per-user/tenant persistence, and robust API fetching.
//   Header row is sticky while scrolling on desktop.
//
// AUD8 follow-up:
//   - Uses authenticated blob preview for avatars.
//   - Browser <img src="/api/users/{id}/avatar/preview"> cannot send Authorization headers.
//   - Keeps tenant-scoped /api/users behaviour intact.
//
// Mobile responsiveness (2026-09-07):
//   - Preserves the existing desktop table, sticky headers, click sorting, and
//     drag/drop column ordering at >= 768px.
//   - Reflows the same table rows into labelled user cards below 768px; there is
//     no duplicate mobile data component and no duplicate AvatarCell mount.
//   - Adds explicit mobile sort controls because desktop headers are hidden.
//
// Mobile height follow-up (2026-09-07):
//   - Sizes the mobile record viewport from the ACTUAL first rendered card height,
//     so the selected Column Chooser fields plus the Manage row are fully visible.
//   - Expands the fixed-height mobile page host only as far as required for one
//     complete card, replacing the desktop-grid-derived mobile height for this widget.
//   - Recalculates after column visibility/order changes and when the first visible
//     record changes; desktop GridLayout sizing remains untouched.
//   - Uses mandatory mobile scroll snapping so each flick settles on a complete user card.
//   - Adds touch-friendly column visibility and up/down ordering controls in the
//     existing column chooser while preserving desktop header drag/drop.
//   - Uses the same users, sortedUsers, visibleKeys, avatar loading, persistence,
//     and Manage action state on desktop and mobile.
//   - Does not add API calls, resize-driven fetching, or client-supplied tenant context.

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import axiosInstance from "../api/axiosInstance";
import { filterAndSortList, getWidgetTitle } from "../utils/widgetUtils";
import ModalWrapper from "../components/ModalWrapper";

const API_BASE = (() => {
  const envBase = import.meta?.env?.VITE_API_BASE;
  if (envBase) return String(envBase).replace(/\/+$/, "");
  const url = new URL(window.location.href);
  const { protocol, hostname, port } = url;
  if (port === "8010" || port === "8070") return `${protocol}//${hostname}:8020/api`;
  if (port === "8020") return `${protocol}//${hostname}:8020/api`;
  return `${url.origin}/api`;
})();

const API = {
  users: `${API_BASE}/users`,
};

const decodeJWT = (token) => {
  try {
    const [, payload] = token.split(".");
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return {};
  }
};

const getUserTenantKey = () => {
  const token =
    localStorage.getItem("anpr_token") || localStorage.getItem("token") || "";
  const p = decodeJWT(token) || {};
  const userId = p.sub || p.user_id || p.uid || "unknownUser";
  const tenantId = p.tenant_id || p.tid || "unknownTenant";
  return { userId, tenantId, token };
};

const storageKey = (userId, tenantId) => `anpr_user_columns_${tenantId}_${userId}`;

const ALL_COLUMNS = [
  { key: "avatar", label: "Avatar" },
  { key: "id", label: "ID" },
  { key: "first_name", label: "First Name" },
  { key: "last_name", label: "Last Name" },
  { key: "username", label: "Username" },
  { key: "email", label: "Email" },
  { key: "role", label: "Role" },
  { key: "is_admin", label: "Admin" },
  { key: "is_active", label: "Active" },
  { key: "department", label: "Department" },
  { key: "job_title", label: "Job Title" },
  { key: "phone_number", label: "Phone" },
  { key: "mobile_number", label: "Mobile" },
  { key: "last_login", label: "Last Login" },
  { key: "created_at", label: "Created" },
  { key: "updated_at", label: "Updated" },
  { key: "avatar_url", label: "Avatar URL" },
];

const DEFAULT_VISIBLE_ORDER = [
  "avatar",
  "first_name",
  "last_name",
  "email",
  "role",
  "is_active",
  "updated_at",
];

const SEARCH_FIELDS = [
  "first_name",
  "last_name",
  "username",
  "email",
  "role",
  "department",
  "job_title",
  "phone_number",
  "mobile_number",
  "avatar_url",
];

const formatCell = (row, key) => {
  const v = row?.[key];

  if (key === "is_admin" || key === "is_active") return v ? "Yes" : "No";

  if (key === "last_login" || key === "created_at" || key === "updated_at") {
    if (!v) return "";
    const d = new Date(v);
    return isNaN(d.getTime()) ? String(v) : d.toLocaleString();
  }

  if (key === "avatar_url") return v || "—";

  return v ?? "";
};

const AvatarCell = ({ user }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const objectUrlRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const clear = () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
      setPreviewUrl(null);
    };

    const load = async () => {
      if (!user?.id || !user?.avatar_url) {
        clear();
        return;
      }

      try {
        const res = await axiosInstance.get(
          `/users/${user.id}/avatar/preview?ts=${Date.now()}`,
          { responseType: "blob" }
        );

        if (cancelled) return;

        if (objectUrlRef.current) {
          URL.revokeObjectURL(objectUrlRef.current);
        }

        const objectUrl = URL.createObjectURL(res.data);
        objectUrlRef.current = objectUrl;
        setPreviewUrl(objectUrl);
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load user avatar preview:", err.response?.data || err);
          clear();
        }
      }
    };

    load();

    return () => {
      cancelled = true;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [user?.id, user?.avatar_url]);

  if (previewUrl) {
    return (
      <img
        src={previewUrl}
        alt="avatar"
        className="w-8 h-8 rounded-full object-cover border"
      />
    );
  }

  return (
    <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-xs text-white">
      {(user?.first_name || "?")[0]}
      {(user?.last_name || "")[0]}
    </div>
  );
};

const ColumnChooserModal = ({
  open,
  onClose,
  allColumns,
  visibilityMap,
  setVisibilityMap,
  order,
  setOrder,
  resetToDefault,
}) => {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const handleToggle = (key) => {
    setVisibilityMap((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const moveColumn = (key, direction) => {
    setOrder((prev) => {
      const fromIndex = prev.indexOf(key);
      const toIndex = fromIndex + direction;

      if (fromIndex < 0 || toIndex < 0 || toIndex >= prev.length) {
        return prev;
      }

      const next = prev.slice();
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const modalContent = (
    <ModalWrapper onClose={onClose}>
      <h3 className="text-lg font-semibold mb-3">Choose Columns</h3>
      <p className="text-sm text-gray-600 mb-3">
        Tick the columns you want to see. Drag desktop table headers to reorder them, or use the mobile up/down controls below.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-[60vh] md:max-h-80 overflow-auto pr-1 md:pr-2">
        {order.map((key, index) => {
          const col = allColumns.find((c) => c.key === key);
          if (!col) return null;
          return (
            <div
              key={key}
              className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 p-2 md:border-0 md:rounded-none md:p-0"
            >
              <label className="flex min-w-0 flex-1 items-center gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={!!visibilityMap[key]}
                  onChange={() => handleToggle(key)}
                  className="h-5 w-5 shrink-0 md:h-auto md:w-auto"
                />
                <span className="truncate">{col.label}</span>
              </label>

              <div className="flex shrink-0 gap-1 md:hidden">
                <button
                  type="button"
                  onClick={() => moveColumn(key, -1)}
                  disabled={index === 0}
                  className="min-h-[40px] min-w-[40px] rounded border border-gray-300 bg-white px-2 text-base font-semibold text-gray-700 disabled:opacity-35"
                  aria-label={`Move ${col.label} up`}
                  title={`Move ${col.label} up`}
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => moveColumn(key, 1)}
                  disabled={index === order.length - 1}
                  className="min-h-[40px] min-w-[40px] rounded border border-gray-300 bg-white px-2 text-base font-semibold text-gray-700 disabled:opacity-35"
                  aria-label={`Move ${col.label} down`}
                  title={`Move ${col.label} down`}
                >
                  ↓
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
        <button
          onClick={resetToDefault}
          className="min-h-[44px] w-full bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white text-sm font-semibold py-2 px-3 rounded shadow sm:w-auto"
        >
          Reset to Default
        </button>
        <button
          onClick={onClose}
          className="min-h-[44px] w-full bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white text-sm font-semibold py-2 px-3 rounded shadow sm:w-auto"
        >
          Done
        </button>
      </div>
    </ModalWrapper>
  );

  const portalTarget = document.getElementById("modal-root") || document.body;
  return createPortal(modalContent, portalTarget);
};

const AdvancedUserListWidget = ({ setSelectedUserId }) => {
  const [users, setUsers] = useState([]);
  const [sortField, setSortField] = useState("first_name");
  const [sortAsc, setSortAsc] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { userId, tenantId, token } = useMemo(getUserTenantKey, []);
  const persistKey = useMemo(() => storageKey(userId, tenantId), [userId, tenantId]);

  const [visibilityMap, setVisibilityMap] = useState(() => {
    const raw = localStorage.getItem(persistKey);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        return parsed.visibilityMap || {};
      } catch {}
    }
    const map = {};
    ALL_COLUMNS.forEach((c) => (map[c.key] = DEFAULT_VISIBLE_ORDER.includes(c.key)));
    return map;
  });

  const [order, setOrder] = useState(() => {
    const raw = localStorage.getItem(persistKey);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed.order) && parsed.order.length) return parsed.order;
      } catch {}
    }
    const defaults = DEFAULT_VISIBLE_ORDER.slice();
    const rest = ALL_COLUMNS.map((c) => c.key).filter((k) => !defaults.includes(k));
    return [...defaults, ...rest];
  });

  const [chooserOpen, setChooserOpen] = useState(false);

  const widgetRootRef = useRef(null);
  const mobileHeaderRef = useRef(null);
  const recordsViewportRef = useRef(null);
  const firstCardRef = useRef(null);

  useEffect(() => {
    const data = { order, visibilityMap, v: 1 };
    localStorage.setItem(persistKey, JSON.stringify(data));
  }, [order, visibilityMap, persistKey]);

  useEffect(() => {
    let cancelled = false;

    const fetchUsers = async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await axiosInstance.get("/users/?limit=1000");
        if (!cancelled) setUsers(Array.isArray(res.data) ? res.data : []);
      } catch (err1) {
        const isNetworkError =
          !err1?.response &&
          (err1?.message?.includes("Network Error") || err1?.message === "Network Error");

        if (!isNetworkError) {
          console.error("❌ Failed to load users:", err1?.response?.data || err1?.message || err1);
          if (!cancelled) setError("Failed to load users");
        } else {
          try {
            const res2 = await axios.get(`${API.users}/?limit=1000`, {
              headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            });
            if (!cancelled) setUsers(Array.isArray(res2.data) ? res2.data : []);
          } catch (err2) {
            console.error(
              "❌ Failed to load users (fallback):",
              err2?.response?.data || err2?.message || err2
            );
            if (!cancelled) setError("Failed to load users");
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchUsers();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const toggleSort = (field) => {
    if (field === "avatar") return;
    if (sortField === field) setSortAsc((a) => !a);
    else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const visibleKeys = useMemo(() => order.filter((k) => visibilityMap[k]), [order, visibilityMap]);

  const dragIndexRef = useRef(null);

  const onHeaderDragStart = (e, key) => {
    const idx = visibleKeys.indexOf(key);
    dragIndexRef.current = idx;
    e.dataTransfer.effectAllowed = "move";
  };

  const onHeaderDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const onHeaderDrop = (e, dropKey) => {
    e.preventDefault();
    const fromIdx = dragIndexRef.current;
    const toIdx = visibleKeys.indexOf(dropKey);
    if (fromIdx === null || fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return;

    const newVisible = visibleKeys.slice();
    const [moved] = newVisible.splice(fromIdx, 1);
    newVisible.splice(toIdx, 0, moved);

    const hidden = order.filter((k) => !visibilityMap[k]);
    setOrder([...newVisible, ...hidden]);
    dragIndexRef.current = null;
  };

  const sortedUsers = useMemo(() => {
    return filterAndSortList(users, searchQuery, SEARCH_FIELDS, sortField, sortAsc);
  }, [users, searchQuery, sortField, sortAsc]);

  useLayoutEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const root = widgetRootRef.current;
    const header = mobileHeaderRef.current;
    const viewport = recordsViewportRef.current;
    if (!root || !header || !viewport) return undefined;

    const mediaQuery = window.matchMedia("(max-width: 767px)");
    let animationFrame = 0;
    let hostRecord = null;

    const restoreHost = () => {
      if (!hostRecord?.node) return;
      hostRecord.node.style.height = hostRecord.height;
      hostRecord.node.style.minHeight = hostRecord.minHeight;
      hostRecord = null;
    };

    const findFixedMobileHost = () => {
      const rootRect = root.getBoundingClientRect();
      const candidates = [];
      let node = root.parentElement;
      let depth = 0;

      while (node && node !== document.body && depth < 6) {
        const rect = node.getBoundingClientRect();
        const inlineHeight = String(node.style?.height || "").trim();
        const widthMatches = Math.abs(rect.width - rootRect.width) < 90;
        const hasFixedInlineHeight =
          inlineHeight &&
          inlineHeight !== "auto" &&
          inlineHeight !== "100%" &&
          inlineHeight !== "100vh";
        if (widthMatches && hasFixedInlineHeight) {
          candidates.push(node);
        }

        node = node.parentElement;
        depth += 1;
      }

      // The nearest fixed-height ancestor is the page's mobile widget host.
      // WidgetWrapper itself uses percentage/flex sizing and is deliberately skipped.
      return candidates.length ? candidates[0] : null;
    };

    const applyMobileSizing = () => {
      cancelAnimationFrame(animationFrame);
      restoreHost();

      viewport.style.height = "";
      viewport.style.flex = "";
      root.style.height = "";
      root.style.minHeight = "";

      if (!mediaQuery.matches) return;

      animationFrame = requestAnimationFrame(() => {
        const firstCard = firstCardRef.current;
        const cardHeight = firstCard
          ? Math.ceil(firstCard.getBoundingClientRect().height)
          : 88;

        // tbody uses p-3 on mobile: 12px above + 12px below the visible card.
        const recordViewportHeight = cardHeight + 24;
        viewport.style.height = `${recordViewportHeight}px`;
        viewport.style.flex = "0 0 auto";

        // The widget root now naturally equals mobile controls + exactly one full card.
        root.style.height = "auto";
        root.style.minHeight = "0px";

        animationFrame = requestAnimationFrame(() => {
          const host = findFixedMobileHost();
          if (!host) return;

          hostRecord = {
            node: host,
            height: host.style.height,
            minHeight: host.style.minHeight,
          };

          const hostRect = host.getBoundingClientRect();
          const rootRect = root.getBoundingClientRect();
          const wrapperChromeHeight = Math.max(0, rootRect.top - hostRect.top);
          const requiredHostHeight = Math.ceil(
            Math.max(host.scrollHeight, wrapperChromeHeight + rootRect.height + 8)
          );

          host.style.height = `${requiredHostHeight}px`;
          host.style.minHeight = `${requiredHostHeight}px`;
        });
      });
    };

    applyMobileSizing();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => applyMobileSizing())
        : null;

    if (resizeObserver) {
      resizeObserver.observe(header);
      if (firstCardRef.current) resizeObserver.observe(firstCardRef.current);
    }

    const handleMediaChange = () => applyMobileSizing();
    mediaQuery.addEventListener?.("change", handleMediaChange);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();
      mediaQuery.removeEventListener?.("change", handleMediaChange);
      restoreHost();
      viewport.style.height = "";
      viewport.style.flex = "";
      root.style.height = "";
      root.style.minHeight = "";
    };
  }, [sortedUsers, visibleKeys]);

  const renderSortIndicator = (field) => {
    if (field !== sortField) return null;
    return sortAsc ? " ▲" : " ▼";
  };

  if (loading) {
    return <p className="text-sm text-gray-500 px-4 py-2 italic">Loading users...</p>;
  }

  if (error) {
    return <p className="text-sm text-red-600 px-4 py-2">{error}</p>;
  }

  return (
    <div
      ref={widgetRootRef}
      className="h-auto min-h-0 w-full overflow-hidden rounded-lg border border-gray-200 shadow-sm bg-white flex flex-col md:h-full"
    >
      <div
        ref={mobileHeaderRef}
        className="bg-accent text-white px-3 py-3 md:px-4 md:py-2 flex flex-col gap-3 md:flex-row md:flex-wrap md:justify-between md:items-center"
      >
        <span className="text-sm font-semibold">
          Search the {getWidgetTitle("advanced_user_list")}
        </span>

        <div className="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-2 md:flex md:w-auto md:items-center">
          <input
            type="text"
            placeholder="Search..."
            className="min-h-[44px] min-w-0 w-full rounded border border-white bg-white px-3 py-2 text-sm text-black md:min-h-0 md:w-48 md:flex-none md:px-2 md:py-1"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button
            type="button"
            onClick={() => setChooserOpen(true)}
            className="min-h-[44px] whitespace-nowrap bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white text-xs font-semibold px-3 py-2 rounded shadow md:min-h-0 md:py-1"
            title="Choose Columns"
          >
            Column Chooser
          </button>
        </div>

        <div className="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-2 md:hidden">
          <label className="flex min-w-0 flex-col gap-1 text-xs font-semibold">
            Sort by
            <select
              value={sortField}
              onChange={(e) => setSortField(e.target.value)}
              className="min-h-[44px] w-full rounded border border-white bg-white px-3 py-2 text-sm font-normal text-black"
            >
              {ALL_COLUMNS.filter((column) => column.key !== "avatar").map((column) => (
                <option key={column.key} value={column.key}>
                  {column.label}
                </option>
              ))}
            </select>
          </label>

          <button
            type="button"
            onClick={() => setSortAsc((current) => !current)}
            className="min-h-[44px] self-end rounded bg-btnGeneralNormal px-4 py-2 text-sm font-semibold text-white shadow hover:bg-btnGeneralHover active:bg-btnGeneralClick"
            aria-label={sortAsc ? "Sort descending" : "Sort ascending"}
            title={sortAsc ? "Currently ascending — tap for descending" : "Currently descending — tap for ascending"}
          >
            {sortAsc ? "▲" : "▼"}
          </button>
        </div>
      </div>

      <div
        ref={recordsViewportRef}
        className="min-h-0 w-full overflow-y-auto overflow-x-hidden overscroll-contain scroll-smooth snap-y snap-mandatory md:flex-1 md:overflow-auto md:snap-none"
      >
        <table className="block w-full text-left text-sm text-gray-700 md:table">
          <thead className="hidden md:table-header-group">
            <tr className="bg-accent text-white select-none">
              {visibleKeys.map((key) => {
                const col = ALL_COLUMNS.find((c) => c.key === key);
                if (!col) return null;

                const clickable = key !== "avatar";
                const stickyClass = "sticky top-0 z-10 bg-accent";

                return (
                  <th
                    key={key}
                    className={`px-4 py-2 ${stickyClass} ${clickable ? "cursor-move" : ""}`}
                    draggable
                    onDragStart={(e) => onHeaderDragStart(e, key)}
                    onDragOver={onHeaderDragOver}
                    onDrop={(e) => onHeaderDrop(e, key)}
                    onClick={clickable ? () => toggleSort(key) : undefined}
                    title="Drag to reorder • Click to sort"
                  >
                    {col.label}
                    {clickable && renderSortIndicator(key)}
                  </th>
                );
              })}

              <th className="px-4 py-2 sticky top-0 z-10 bg-accent">Manage</th>
            </tr>
          </thead>

          <tbody className="block space-y-3 p-3 md:table-row-group md:space-y-0 md:p-0">
            {sortedUsers.length === 0 ? (
              <tr className="block md:table-row">
                <td
                  colSpan={visibleKeys.length + 1}
                  className="block rounded-lg border border-gray-200 bg-white px-4 py-5 text-center text-gray-500 italic md:table-cell md:rounded-none md:border-0"
                >
                  No users found.
                </td>
              </tr>
            ) : (
              sortedUsers.map((user, index) => (
                <tr
                  key={user.id}
                  ref={index === 0 ? firstCardRef : undefined}
                  className="block snap-start scroll-mt-3 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm md:table-row md:rounded-none md:border-x-0 md:border-t-0 md:border-b md:border-gray-100 md:shadow-none md:odd:bg-gray-50 md:even:bg-white"
                >
                  {visibleKeys.map((key) => {
                    const col = ALL_COLUMNS.find((column) => column.key === key);
                    if (!col) return null;

                    if (key === "avatar") {
                      return (
                        <td
                          key={key}
                          className="flex items-center justify-between gap-4 border-b border-gray-100 px-4 py-3 md:table-cell md:border-b-0 md:px-4 md:py-2"
                        >
                          <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-gray-500 md:hidden">
                            {col.label}
                          </span>
                          <AvatarCell user={user} />
                        </td>
                      );
                    }

                    return (
                      <td
                        key={key}
                        className="flex items-start justify-between gap-4 border-b border-gray-100 px-4 py-3 md:table-cell md:border-b-0 md:px-4 md:py-2"
                      >
                        <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-gray-500 md:hidden">
                          {col.label}
                        </span>
                        <span className="min-w-0 break-words text-right md:text-left">
                          {formatCell(user, key)}
                        </span>
                      </td>
                    );
                  })}

                  <td className="block px-4 py-3 md:table-cell md:px-4 md:py-2">
                    <button
                      type="button"
                      className="min-h-[44px] w-full bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white font-body text-sm px-3 py-2 rounded shadow transition-colors duration-200 ease-in-out md:min-h-0 md:w-auto md:text-xs md:py-1"
                      title="Manage User"
                      onClick={() => setSelectedUserId?.(user.id)}
                    >
                      Manage
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ColumnChooserModal
        open={chooserOpen}
        onClose={() => setChooserOpen(false)}
        allColumns={ALL_COLUMNS}
        visibilityMap={visibilityMap}
        setVisibilityMap={setVisibilityMap}
        order={order}
        setOrder={setOrder}
        resetToDefault={() => {
          const map = {};
          ALL_COLUMNS.forEach((c) => (map[c.key] = DEFAULT_VISIBLE_ORDER.includes(c.key)));
          setVisibilityMap(map);

          const defaults = DEFAULT_VISIBLE_ORDER.slice();
          const rest = ALL_COLUMNS.map((c) => c.key).filter((k) => !defaults.includes(k));
          setOrder([...defaults, ...rest]);

          setSortField("first_name");
          setSortAsc(true);
        }}
      />
    </div>
  );
};

export default AdvancedUserListWidget;